import { useState } from "react";
import toast from "react-hot-toast";
import { X } from "lucide-react";
import {
  useSupplierPayables,
  useSupplierPayable,
  useRegisterSupplierPayment,
  useSupplierPayableSummary,
  useUpdateSupplierPayableDueDate,
  SUPPLIER_PAYABLE_STATUS_LABEL,
  SUPPLIER_PAYMENT_METHOD_LABEL,
  SupplierPayableStatus,
  SupplierPaymentMethod,
} from "@/api/supplier-payables";
import { getApiErrorMessage } from "@/api/client";

const currency = new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 });

const todayStr = () => new Date().toISOString().slice(0, 10);

const STATUS_STYLE: Record<SupplierPayableStatus, string> = {
  PENDIENTE: "bg-amber-100 text-amber-700",
  PARCIAL: "bg-blue-100 text-blue-700",
  PAGADA: "bg-green-100 text-green-700",
  ANULADA: "bg-red-100 text-red-700",
};

/** Cuentas por Pagar a Proveedores: lo que se le debe a cada proveedor por compras ya recibidas, y los abonos hechos. Nace automáticamente al recibir una compra — ver supplier-payable.service.ts. */
export default function SupplierPayablesPage() {
  const [status, setStatus] = useState<SupplierPayableStatus | "">("PENDIENTE");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading } = useSupplierPayables({ status: status || undefined, pageSize: 50 });
  const { data: summary } = useSupplierPayableSummary();
  const payables = data?.data ?? [];

  const totalPending = summary?.reduce((sum, s) => sum + s.pendingBalance, 0) ?? 0;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Cuentas por pagar</h1>
        <p className="text-sm text-gray-500">Lo que se le debe a cada proveedor por compras ya recibidas, y los abonos registrados.</p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="card p-4">
          <p className="text-xs font-medium uppercase text-gray-500">Proveedores con saldo</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{summary?.length ?? 0}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs font-medium uppercase text-gray-500">Saldo pendiente total</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{currency.format(totalPending)}</p>
        </div>
      </div>

      {summary && summary.length > 0 && (
        <div className="card overflow-hidden">
          <div className="border-b border-gray-100 px-4 py-3">
            <h2 className="text-sm font-semibold text-gray-900">Deuda por proveedor</h2>
          </div>
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-gray-500">Proveedor</th>
                <th className="px-4 py-2 text-right font-medium text-gray-500">Cuentas pendientes</th>
                <th className="px-4 py-2 text-right font-medium text-gray-500">Saldo pendiente</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {summary.map((s) => (
                <tr key={s.supplierId}>
                  <td className="px-4 py-2 text-gray-900">{s.supplierName}</td>
                  <td className="px-4 py-2 text-right text-gray-600">{s.pendingCount}</td>
                  <td className="px-4 py-2 text-right text-gray-900">{currency.format(s.pendingBalance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card flex flex-wrap items-center gap-3 p-4">
        <select className="input-field w-auto" value={status} onChange={(e) => setStatus(e.target.value as SupplierPayableStatus | "")}>
          <option value="">Todos los estados</option>
          {Object.entries(SUPPLIER_PAYABLE_STATUS_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Compra</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Proveedor</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Sucursal</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Original</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Saldo</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Vence</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Estado</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {!isLoading && payables.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-gray-400">
                  No hay cuentas por pagar con este filtro.
                </td>
              </tr>
            )}
            {payables.map((p) => {
              const isOverdue = !!p.dueDate && p.dueDate < todayStr() && (p.status === "PENDIENTE" || p.status === "PARCIAL");
              return (
                <tr key={p.id} className="cursor-pointer hover:bg-gray-50" onClick={() => setSelectedId(p.id)}>
                  <td className="px-4 py-3 text-gray-600">{p.purchase?.invoiceNumber ?? p.purchase?.purchaseDate ?? "—"}</td>
                  <td className="px-4 py-3 text-gray-900">{p.supplier?.name ?? "—"}</td>
                  <td className="px-4 py-3 text-gray-600">{p.branch?.name ?? "—"}</td>
                  <td className="px-4 py-3 text-right text-gray-600">{currency.format(Number(p.principal))}</td>
                  <td className="px-4 py-3 text-right font-medium text-gray-900">{currency.format(Number(p.balance))}</td>
                  <td className={`px-4 py-3 ${isOverdue ? "font-medium text-red-600" : "text-gray-600"}`}>{p.dueDate ?? "—"}{isOverdue ? " (vencida)" : ""}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[p.status]}`}>{SUPPLIER_PAYABLE_STATUS_LABEL[p.status]}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button type="button" className="text-xs font-medium text-brand-600 hover:text-brand-800" onClick={() => setSelectedId(p.id)}>
                      Ver / Abonar
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selectedId && <PayableDetailModal id={selectedId} onClose={() => setSelectedId(null)} />}
    </div>
  );
}

const PAYMENT_METHODS = Object.entries(SUPPLIER_PAYMENT_METHOD_LABEL) as Array<[SupplierPaymentMethod, string]>;

function PayableDetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { data: payable, isLoading } = useSupplierPayable(id);
  const registerPayment = useRegisterSupplierPayment();
  const updateDueDate = useUpdateSupplierPayableDueDate();
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<SupplierPaymentMethod>("TRANSFERENCIA");
  const [notes, setNotes] = useState("");

  const handleDueDateChange = async (value: string) => {
    try {
      await updateDueDate.mutateAsync({ id, dueDate: value || null });
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handlePay = async () => {
    if (!amount || Number(amount) <= 0) {
      toast.error("Ingresa un monto de abono válido.");
      return;
    }
    try {
      await registerPayment.mutateAsync({ id, amount, paymentMethod, notes: notes || undefined });
      toast.success("Abono registrado.");
      setAmount("");
      setNotes("");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Detalle de cuenta por pagar</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          {isLoading && <p className="text-sm text-gray-400">Cargando...</p>}
          {payable && (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-500">Proveedor</p>
                  <p className="font-medium text-gray-900">{payable.supplier?.name ?? "—"}</p>
                </div>
                <div>
                  <p className="text-gray-500">Compra</p>
                  <p className="font-medium text-gray-900">{payable.purchase?.invoiceNumber ?? payable.purchase?.purchaseDate ?? "—"}</p>
                </div>
                <div>
                  <p className="text-gray-500">Original</p>
                  <p className="font-medium text-gray-900">{currency.format(Number(payable.principal))}</p>
                </div>
                <div>
                  <p className="text-gray-500">Saldo pendiente</p>
                  <p className="font-semibold text-gray-900">{currency.format(Number(payable.balance))}</p>
                </div>
                <div>
                  <p className="text-gray-500">Fecha de vencimiento</p>
                  <input
                    type="date"
                    className="input-field mt-0.5"
                    defaultValue={payable.dueDate ?? ""}
                    disabled={payable.status === "ANULADA"}
                    onBlur={(e) => {
                      if (e.target.value !== (payable.dueDate ?? "")) handleDueDateChange(e.target.value);
                    }}
                  />
                </div>
              </div>

              {payable.payments.length > 0 && (
                <div>
                  <p className="mb-1 text-sm font-medium text-gray-700">Abonos registrados</p>
                  <ul className="space-y-1 text-xs text-gray-600">
                    {payable.payments.map((p) => (
                      <li key={p.id} className="flex justify-between">
                        <span>
                          {p.paymentDate} · {SUPPLIER_PAYMENT_METHOD_LABEL[p.paymentMethod]}
                          {p.createdBy ? ` · ${p.createdBy.fullName}` : ""} {p.notes ? `· ${p.notes}` : ""}
                        </span>
                        <span className="font-medium text-gray-900">{currency.format(Number(p.amount))}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(payable.status === "PENDIENTE" || payable.status === "PARCIAL") && (
                <div className="space-y-2 rounded-md bg-gray-50 p-3">
                  <p className="text-sm font-medium text-gray-700">Registrar abono</p>
                  <div className="flex gap-2">
                    <input className="input-field" type="number" placeholder="Monto" value={amount} onChange={(e) => setAmount(e.target.value)} />
                    <button type="button" className="btn-secondary" onClick={() => setAmount(String(Number(payable.balance)))}>
                      Todo
                    </button>
                  </div>
                  <select className="input-field" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value as SupplierPaymentMethod)}>
                    {PAYMENT_METHODS.map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                  <input className="input-field" placeholder="Nota (opcional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
                  <button type="button" className="btn-primary w-full" disabled={registerPayment.isPending} onClick={handlePay}>
                    {registerPayment.isPending ? "Registrando..." : "Registrar abono"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
